import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { locationsAPI, exportAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import RequireCap from '../components/auth/RequireCap';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, MapPin, Warehouse, Building2, Pencil, Trash2, Download, Package, DollarSign, AlertTriangle } from 'lucide-react';
import { formatCurrency } from '../lib/utils';
import api from '../lib/api';

const emptyLocation = { name: '', code: '', type: 'warehouse', address: '', is_active: true, tax_rate: '', tax_jurisdiction: '' };

export default function Locations() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyLocation });

  useEffect(() => { loadLocations(); }, []);

  const loadLocations = async () => {
    try {
      const [locRes, ovRes] = await Promise.all([
        locationsAPI.list(),
        api.get('/locations/dashboard/overview'),
      ]);
      setLocations(locRes.data);
      setOverview(ovRes.data);
    } catch (error) {
      console.error('Failed to load locations:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyLocation });
    setShowDialog(true);
  };

  const openEdit = (loc) => {
    setEditingId(loc.id);
    setForm({
      name: loc.name, code: loc.code, type: loc.type, address: loc.address || '', is_active: loc.is_active,
      tax_rate: loc.tax_rate ?? '', tax_jurisdiction: loc.tax_jurisdiction || ''
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.code) { toast.error('Name and code are required'); return; }
    if (form.tax_rate !== '' && (isNaN(parseFloat(form.tax_rate)) || parseFloat(form.tax_rate) < 0)) {
      toast.error('Tax rate must be a positive number'); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        tax_rate: form.tax_rate === '' ? null : parseFloat(form.tax_rate),
        tax_jurisdiction: form.tax_jurisdiction || null,
      };
      if (editingId) {
        await locationsAPI.update(editingId, payload);
        toast.success('Location updated');
      } else {
        await locationsAPI.create(payload);
        toast.success('Location created');
      }
      setShowDialog(false);
      setForm({ ...emptyLocation });
      setEditingId(null);
      loadLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save location');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (loc) => {
    if (!window.confirm(`Delete "${loc.name}"? This cannot be undone.`)) return;
    try {
      await locationsAPI.delete(loc.id);
      toast.success('Location deleted');
      loadLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete location');
    }
  };

  return (
    <div className="space-y-6" data-testid="locations-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Warehouses</h1>
          <p className="text-slate-500 mt-1">Manage warehouses and their stocking locations</p>
        </div>
        <div className="flex gap-3">
          <RequireCap module="employees" action="export">
            <Button variant="outline" onClick={async () => {
              try {
                const res = await exportAPI.locations();
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement('a'); a.href = url; a.download = 'locations.csv'; a.click();
              } catch { toast.error('Export failed'); }
            }} data-testid="export-locations-btn">
              <Download className="w-4 h-4 mr-2" />Export CSV
            </Button>
          </RequireCap>
          <RequireCap module="employees" action="add">
            <Button onClick={openCreate} data-testid="add-location-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Warehouse
            </Button>
          </RequireCap>
        </div>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Warehouse' : 'Add Warehouse'}</DialogTitle>
            <DialogDescription>{editingId ? 'Update location details' : 'Create a new warehouse or site location'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Main Warehouse" data-testid="location-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Code *</Label>
                <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="WH-001" data-testid="location-code-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="location-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="warehouse">Warehouse</SelectItem>
                  <SelectItem value="site">Site</SelectItem>
                  <SelectItem value="office">Office</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Address</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="123 Main St" data-testid="location-address-input" />
            </div>
            <div className="border-t pt-4 space-y-4">
              <p className="text-sm font-semibold text-slate-700">Sales Tax</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Combined Tax Rate (%)</Label>
                  <Input
                    type="number"
                    step="0.0001"
                    min="0"
                    value={form.tax_rate}
                    onChange={(e) => setForm({ ...form, tax_rate: e.target.value })}
                    placeholder="e.g. 8.625"
                    data-testid="location-tax-rate-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tax Jurisdiction</Label>
                  <Input
                    value={form.tax_jurisdiction}
                    onChange={(e) => setForm({ ...form, tax_jurisdiction: e.target.value })}
                    placeholder="Oklahoma City, Oklahoma County, OK"
                    data-testid="location-tax-jurisdiction-input"
                  />
                </div>
              </div>
              {editingId && (() => {
                const loc = locations.find(l => l.id === editingId);
                return loc?.tax_last_verified ? (
                  <p className="text-xs text-slate-500" data-testid="location-tax-last-verified">
                    Rate last verified: {loc.tax_last_verified}
                  </p>
                ) : null;
              })()}
            </div>
            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch checked={form.is_active} onCheckedChange={(checked) => setForm({ ...form, is_active: checked })} />
            </div>
            <Button onClick={handleSave} disabled={saving} className="w-full" data-testid="save-location-btn">
              {saving ? 'Saving...' : editingId ? 'Update Location' : 'Create Location'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Card>
        {loading ? (
          <div className="p-8 space-y-4">{[...Array(3)].map((_, i) => <div key={i} className="h-16 skeleton-pulse rounded"></div>)}</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Warehouse</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Tax Rate</TableHead>
                <TableHead className="text-right">Units</TableHead>
                <TableHead className="text-right">Value</TableHead>
                <TableHead className="text-right">IT Assets</TableHead>
                <TableHead>Status</TableHead>
                {hasRole('admin') && <TableHead className="w-24">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {locations.map(loc => {
                const ov = overview.find(o => o.id === loc.id) || {};
                return (
                <TableRow key={loc.id} data-testid={`location-row-${loc.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate(`/locations/${loc.id}/dashboard`)}>
                      <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                        {loc.type === 'warehouse' ? <Warehouse className="w-5 h-5 text-slate-600" /> : <Building2 className="w-5 h-5 text-slate-600" />}
                      </div>
                      <div>
                        <span className="font-medium text-blue-600 hover:underline">{loc.name}</span>
                        {ov.low_stock_count > 0 && <span className="ml-2"><AlertTriangle className="w-3 h-3 text-red-500 inline" /></span>}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono">{loc.code}</TableCell>
                  <TableCell><Badge variant="outline" className="capitalize">{loc.type}</Badge></TableCell>
                  <TableCell className="text-right font-bold">{(ov.total_units || 0).toLocaleString()}</TableCell>
                  <TableCell className="text-right text-emerald-600">{formatCurrency(ov.total_value || 0)}</TableCell>
                  <TableCell className="text-right">{ov.it_assets_count || 0}</TableCell>
                  <TableCell><Badge variant={loc.is_active ? 'default' : 'secondary'}>{loc.is_active ? 'Active' : 'Inactive'}</Badge></TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-1">
                      <RequireCap module="employees" action="edit">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(loc)} data-testid={`edit-location-${loc.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                      </RequireCap>
                      <RequireCap module="employees" action="delete">
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(loc)} data-testid={`delete-location-${loc.id}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </RequireCap>
                    </div>
                  </TableCell>
                </TableRow>
                );
              })}
              {locations.length === 0 && (
                <TableRow>
                  <TableCell colSpan={hasRole('admin') ? 8 : 7} className="text-center py-12 text-slate-500">
                    <MapPin className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>No locations configured</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
